import { usePathname, useRouter } from "next/navigation";
import { useMemo } from "react";

// Components
import { SelectSorter } from "src/components/select-sorter";
import { SelectProvider } from "@/components/select-provider";

// Definitions
import { MultiValue, SingleValue } from "react-select";
import {
  type FilterStaticQueryParamsType,
  SORT_BASE_KEYS,
  SORT_ORDER_KEYS,
  SORT_PARAM_KEYS,
} from "@/types/filter";
import type { SelectItemType } from "@/components/select-sorter";
import type { ProviderSelectItemType } from "@/components/select-provider";

// Utils
import st from "./index.module.scss";

type FilterStaticProps = {
  searchParams: FilterStaticQueryParamsType & { provider?: string };
  optionsSort?: SelectItemType[];
  optionsProvider?: ProviderSelectItemType[];
  providerPlaceholder?: React.ReactNode;
  providerReset?: React.FC;
};

export default function FilterStatic(props: FilterStaticProps) {
  const { searchParams, optionsSort, optionsProvider, providerPlaceholder, providerReset } = props;

  const router = useRouter();
  const pathname = usePathname();

  const initialValueFilterProvider = useMemo(() => {
    const providerParam = searchParams.provider;
    if (!providerParam) {
      return null;
    }
    return optionsProvider?.find((option) => option.value === providerParam);
  }, [searchParams, optionsProvider]);

  const querySortQueryOption = useMemo(
    () =>
      optionsSort?.find((option) => {
        const searchParamValues = Object.values(searchParams);
        const availableSortOrderValues = Object.values({ ...SORT_BASE_KEYS, ...SORT_ORDER_KEYS });
        const querySortValue = searchParamValues.find((current) => {
          return availableSortOrderValues.find((el) => {
            return el === current;
          });
        });
        return option.value === querySortValue;
      }),
    [searchParams, optionsSort],
  );

  const initialValueSort = querySortQueryOption || optionsSort?.[0];

  const handleChangeSort = (newValue: SingleValue<SelectItemType> | MultiValue<SelectItemType>) => {
    const qp = new URLSearchParams(searchParams as Record<string, string>);
    const qpKeys = Object.keys(searchParams);
    const isMulti = Array.isArray(newValue);
    const val: SelectItemType = isMulti ? newValue[0] : newValue;
    if (val?.field) {
      const prevSortField = Object.keys(SORT_PARAM_KEYS).find((el) => el !== val.field);
      if (prevSortField && qpKeys.includes(prevSortField)) {
        qp.delete(prevSortField);
      }
      if (qpKeys.includes("page")) {
        qp.delete("page");
      }
      qp.set(val?.field, val?.value);
    }
    router.push(pathname + "?" + qp.toString(), { scroll: false });
  };

  const handleChangeProvider = (
    newValue: SingleValue<ProviderSelectItemType> | MultiValue<ProviderSelectItemType>,
  ) => {
    const qp = new URLSearchParams(searchParams as Record<string, string>);
    const qpKeys = Object.keys(searchParams);
    const isMulti = Array.isArray(newValue);
    const val: SelectItemType = isMulti ? newValue[0] : newValue;
    if (!val) {
      qp.delete("provider");
    }
    if (val) {
      qp.set("provider", val?.value);
    }
    if (qpKeys.includes("page")) {
      qp.delete("page");
    }
    router.push(pathname + "?" + qp.toString(), { scroll: false });
  };

  return (
    <form className={st.form}>
      <div className={st.actions}>
        {optionsProvider && (
          <div className={st["filter-provider"]}>
            <SelectProvider
              name={SORT_PARAM_KEYS.order}
              options={optionsProvider}
              defaultValue={initialValueFilterProvider}
              isSearchable={false}
              onChange={handleChangeProvider}
              providerReset={providerReset}
              placeholder={providerPlaceholder}
              isClearable
            />
          </div>
        )}
        {optionsSort && (
          <div className={st.sorter}>
            <SelectSorter
              name={SORT_PARAM_KEYS.order}
              options={optionsSort}
              defaultValue={initialValueSort}
              isSearchable={false}
              hideSelectedOptions
              onChange={handleChangeSort}
            />
          </div>
        )}
      </div>
    </form>
  );
}
